/**
 * CountdownTimer.jsx
 *
 * Ticks down to a unix-timestamp target (e.g. gtdStartTime,
 * publicStartTime). Renders nothing fancy on its own — pass a render
 * prop-free "label" wrapper around it via className/size; when the
 * target has passed it calls onComplete once and shows "LIVE".
 */
import React, { useEffect, useRef, useState } from 'react';
import GlowText from '../GlowText';

function splitDuration(totalSeconds) {
  const clamped = Math.max(0, totalSeconds);
  const days = Math.floor(clamped / 86400);
  const hours = Math.floor((clamped % 86400) / 3600);
  const minutes = Math.floor((clamped % 3600) / 60);
  const seconds = Math.floor(clamped % 60);
  return { days, hours, minutes, seconds };
}

export default function CountdownTimer({ target, onComplete, variant = 'cyan', size = 22, clockSkew = 0 }) {
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));
  const firedRef = useRef(false);

  useEffect(() => {
    const id = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(id);
  }, []);

  // Anchor to chain time: `clockSkew` is (block.timestamp - device clock)
  // from saleState(), so a visitor with a wrong system clock still sees the
  // correct time remaining instead of a countdown that's hours out.
  const remaining = target ? target - (now + clockSkew) : null;

  useEffect(() => {
    if (remaining !== null && remaining <= 0 && !firedRef.current) {
      firedRef.current = true;
      onComplete && onComplete();
    }
    if (remaining !== null && remaining > 0) {
      firedRef.current = false;
    }
  }, [remaining, onComplete]);

  if (!target) {
    return <GlowText text="NOT SCHEDULED" size={size} variant="violet" />;
  }
  if (remaining <= 0) {
    return <GlowText text="LIVE" size={size} variant="cyan" />;
  }

  const { days, hours, minutes, seconds } = splitDuration(remaining);
  const pad = (n) => String(n).padStart(2, '0');
  const parts = days > 0
    ? `${days}D ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
    : `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;

  return <GlowText text={parts} size={size} variant={variant} mono />;
}
